import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/firebaseConfig';
import { ref, update, onValue, query, orderByChild, equalTo, set, push, get, increment } from 'firebase/database';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import StatsCard from '../components/StatsCard';
import { generateId } from '../firebase/idGenerator';
import { useNotifications } from '../hooks/useNotifications';
import { createNotification, sendTemplateNotification } from '../services/notificationService';
import AIAnalyst from './AIAnalyst';
import TrackingModal from '../components/TrackingModal';
import { parseResumeContent, simulateNeuralProcessing, extractFromBlob } from '../services/resumeParserService';
import { useRef } from 'react';

// Modular Components
import ProfileEditor from '../components/Dashboard/ProfileEditor';
import SecurityPanel from '../components/Dashboard/SecurityPanel';
import NotificationPanel from '../components/Dashboard/NotificationPanel';

import './Dashboard.css';

function Dashboard() {
  const { userProfile, currentUser } = useAuth();
  const { notifications, unreadCount, deleteNotification } = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  
  // Local state for the form
  const [formData, setFormData] = useState({
    githubUsername: "",
    leetcodeUsername: "",
    codeforcesUsername: "",
    gfgUsername: "",
    bio: "",
    avatar: "",
    name: "",
    experience: [],
    education: [],
    projects: [],
    certifications: [],
    phone: "",
    linkedin: "",
    summary: "",
    otherInfo: "",
    achievements: []
  });
  
  const [saving, setSaving] = useState(false);
  const [idChangeStep, setIdChangeStep] = useState(1);
  const [requestedId, setRequestedId] = useState("");
  const [generatedCaseId, setGeneratedCaseId] = useState("");
  const [message, setMessage] = useState("");
  const [modalType, setModalType] = useState(null); // 'purge' or 'report'
  const [purgeStep, setPurgeStep] = useState(1);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [reportData, setReportData] = useState({ category: 'bug', subject: '', details: '' });
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [selectedTrackingCase, setSelectedTrackingCase] = useState(null);
  const [selectedNotif, setSelectedNotif] = useState(null);
  
  // New Settings State
  const [settingsData, setSettingsData] = useState({
    embedTheme: 'dark',
    profileVisibility: 'public',
    notifyUpdates: true,
    notifySecurity: true
  });

  const [resumeMode, setResumeMode] = useState(userProfile?.resumeMode || false);
  const [myReports, setMyReports] = useState([]);
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionText, setExtractionText] = useState("");
  const [extractionError, setExtractionError] = useState("");
  const [extractionFile, setExtractionFile] = useState(null);
  const [isTextMode, setIsTextMode] = useState(false);
  const [extractionType, setExtractionType] = useState('text'); // 'text', 'image', 'pdf'
  const fileInputRef = useRef(null);

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const handleCopyEmbed = () => {
    const embedCode = `<iframe src="${window.location.origin}/embed/${userProfile.profileId}?theme=${settingsData.embedTheme}" width="100%" height="600px" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    setCopiedEmbed(true);
    setTimeout(() => setCopiedEmbed(false), 2000);
  };

  const toggleResumeMode = async () => {
    const newMode = !resumeMode;
    setResumeMode(newMode);
    try {
      await update(ref(db, "users/" + currentUser.uid), { resumeMode: newMode });
    } catch (err) {
      console.error("Resume mode toggle failed", err);
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFormData({
        githubUsername: userProfile.githubUsername || "",
        leetcodeUsername: userProfile.leetcodeUsername || "",
        codeforcesUsername: userProfile.codeforcesUsername || "",
        gfgUsername: userProfile.gfgUsername || "",
        bio: userProfile.bio || "",
        avatar: userProfile.avatar || "",
        name: userProfile.name || "",
        experience: userProfile.experience || [],
        education: userProfile.education || [],
        projects: userProfile.projects || [],
        certifications: userProfile.certifications || [],
        phone: userProfile.phone || "",
        linkedin: userProfile.linkedin || "",
        summary: userProfile.summary || "",
        otherInfo: userProfile.otherInfo || "",
        achievements: userProfile.achievements || []
      });
      setSettingsData({
        embedTheme: userProfile.settings?.embedTheme || 'dark',
        profileVisibility: userProfile.settings?.profileVisibility || 'public',
        notifyUpdates: userProfile.settings?.notifyUpdates ?? true,
        notifySecurity: userProfile.settings?.notifySecurity ?? true
      });
      setResumeMode(userProfile.resumeMode || false);
    }
  }, [userProfile]);

  // Fetch User Reports
  useEffect(() => {
    if (!currentUser) return;

    const reportsRef = ref(db, 'reports');
    const userReportsQuery = query(reportsRef, orderByChild('uid'), equalTo(currentUser.uid));

    const unsubscribe = onValue(userReportsQuery, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.values(data).sort((a, b) => b.createdAt - a.createdAt);
        setMyReports(list);
      } else {
        setMyReports([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Mark notifications as seen
  useEffect(() => {
    if (activeTab === 'notifications') {
      localStorage.setItem('notif_last_seen', Date.now());
      // A small hack to trigger unreadCount update in useNotifications hook 
      // since it listens to localStorage implicitly via onValue callback
      // but if we want it instant, we might need an event emitter or window event.
      // However, onValue will usually re-trigger if anything changes.
      // To be safe, we can manually trigger a window storage event
      window.dispatchEvent(new Event('storage'));
    }
  }, [activeTab]);

  // Handle Platform Connection Warning (Origin Core Audit)
  useEffect(() => {
    if (userProfile && currentUser) {
      const hasPlatforms = 
        userProfile.githubUsername || 
        userProfile.leetcodeUsername || 
        userProfile.codeforcesUsername || 
        userProfile.gfgUsername;
      
      const lastWarning = sessionStorage.getItem('platform_warning_sent');

      if (!hasPlatforms && !lastWarning) {
        // Trigger the professional warning template
        sendTemplateNotification(currentUser.uid, 'INTEGRATION_MISSING');
        sessionStorage.setItem('platform_warning_sent', 'true');
      }
    }
  }, [userProfile, currentUser]);

  // Handle Legacy Accounts migration
  useEffect(() => {
    if (userProfile && currentUser) {
      // Self-healing: Generate EID/SUID for legacy accounts
      const rotateSecurityKeys = async () => {
        if (!userProfile.eid || !userProfile.suid) {
          console.log("Legacy account detected. Provisioning secure IDs...");
          const updates = {};
          if (!userProfile.eid) {
            updates.eid = await generateId('EID', 'users', 'eid');
          }
          if (!userProfile.suid) {
            updates.suid = await generateId('SUID', 'users', 'suid');
          }
          
          try {
            await update(ref(db, "users/" + currentUser.uid), updates);
            console.log("Migration successful:", updates);
          } catch (err) {
            console.error("Migration failed:", err);
          }
        }
      };
      rotateSecurityKeys();
    }
  }, [userProfile, currentUser.uid]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value.trim() });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage("");

    try {
      const userRef = ref(db, "users/" + currentUser.uid);
      await update(userRef, {
        githubUsername: formData.githubUsername,
        leetcodeUsername: formData.leetcodeUsername,
        codeforcesUsername: formData.codeforcesUsername,
        gfgUsername: formData.gfgUsername,
        bio: formData.bio,
        avatar: formData.avatar,
        name: formData.name,
        experience: formData.experience,
        education: formData.education,
        projects: formData.projects,
        certifications: formData.certifications,
        phone: formData.phone,
        linkedin: formData.linkedin,
        summary: formData.summary,
        otherInfo: formData.otherInfo,
        achievements: formData.achievements,
        settings: settingsData
      });
      await sendTemplateNotification(currentUser.uid, 'SETTINGS_UPDATED');
      setMessage("Profile and Settings updated successfully!");
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setMessage("Error updating profile. Please try again.");
      console.error(err);
    }
    setSaving(false);
  };

  const handleArrayUpdate = (type, action, index, data) => {
    let newList = [...(formData[type] || [])];
    if (action === 'add') {
      newList.push(data);
    } else if (action === 'remove') {
      newList.splice(index, 1);
    } else if (action === 'edit') {
      newList[index] = { ...newList[index], ...data };
    }
    setFormData({ ...formData, [type]: newList });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExtractionFile(file);
      setIsTextMode(false);
      const type = file.type.includes('image') ? 'image' : file.type.includes('pdf') ? 'pdf' : 'text';
      setExtractionType(type);
    }
  };

  const handleExtract = async () => {
    // Validation
    if (isTextMode && !extractionText.trim()) {
      return setExtractionError("Please provide resume content for analysis.");
    }
    if (!isTextMode && !extractionFile) {
      return setExtractionError("Please select a document for neural scanning.");
    }
    
    setIsExtracting(true);
    setExtractionError("");
    
    try {
      let rawText = "";
      
      if (isTextMode) {
        rawText = extractionText;
        setExtractionType('text');
      } else {
        // Multi-format extraction (OCR or PDF)
        rawText = await extractFromBlob(extractionFile);
      }

      await simulateNeuralProcessing(3500); // High-fidelity processing delay
      const parsedData = parseResumeContent(rawText);
      
      // Merge parsed data into formData (avoiding overwriting if fields exist)
      setFormData(prev => {
        const next = { ...prev };
        
        // 1. Map Atomic Fields if empty
        if (!next.name && parsedData.name) next.name = parsedData.name;
        if (!next.bio && parsedData.bio) next.bio = parsedData.bio;
        if (!next.summary && parsedData.summary) next.summary = parsedData.summary;
        if (!next.phone && parsedData.phone) next.phone = parsedData.phone;
        if (!next.linkedin && parsedData.linkedin) next.linkedin = parsedData.linkedin;
        // email is stored in userProfile.email usually, but if we want to store it in formData:
        // if (!next.email && parsedData.email) next.email = parsedData.email; 
        // Our formData doesn't have an 'email' field currently, it uses userProfile.email.
        // But if we want to add it to 'otherInfo' or just log it:
        if (parsedData.email && !next.bio.includes(parsedData.email)) {
           console.log("Detected Email for Dossier:", parsedData.email);
        }

        // 2. Deduplication Layer for Arrays
        const dedupe = (existing, extracted, key) => {
          const keys = new Set(existing.map(item => (item[key] || "").toLowerCase()));
          return [...existing, ...extracted.filter(item => !keys.has((item[key] || "").toLowerCase()))];
        };

        if (parsedData.experience.length > 0) {
          next.experience = dedupe(next.experience, parsedData.experience, 'role');
        }
        if (parsedData.education.length > 0) {
          next.education = dedupe(next.education, parsedData.education, 'degree');
        }
        if (parsedData.projects.length > 0) {
          next.projects = dedupe(next.projects, parsedData.projects, 'title');
        }

        return next;
      });
      
      setShowExtractModal(false);
      setExtractionText("");
      setExtractionFile(null);
      setMessage(`Neural extraction (${extractionType.toUpperCase()}) successful. Review your dossier.`);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error("Extraction Failed", err);
      setExtractionError(`Neural engine error: ${err.message || 'Check document format'}`);
    } finally {
      setIsExtracting(false);
    }
  };


  const handleSettingsChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettingsData(prev => ({ ...prev, [e.target.name]: value }));
  };

  const submitQuickReport = async (e) => {
    e.preventDefault();
    if (!reportData.subject || !reportData.details) return alert("Please fill all fields.");
    
    try {
      setSaving(true);
      const reportId = `REP-${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0').toUpperCase()}`;
      await update(ref(db, 'reports/' + reportId), {
        reportId,
        uid: currentUser.uid,
        userName: userProfile.name,
        userEmail: userProfile.email,
        suid: userProfile.suid || 'MISSING',
        category: reportData.category,
        subject: reportData.subject,
        details: reportData.details,
        status: 'open',
        priority: reportData.category === 'security' ? 'high' : 'medium',
        origin: 'User Portal',
        createdAt: Date.now()
      });
      setModalType(null);
      setReportData({ category: 'bug', subject: '', details: '' });
      alert("Report Submitted Successfully. You can track its status in the Security tab.");
    } catch (err) {
      alert("Failed to submit report.");
    } finally {
      setSaving(false);
    }
  };

  const requestAccountPurge = async () => {
    try {
      setSaving(true);
      const reportId = `REP-${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0').toUpperCase()}`;
      const newReportRef = ref(db, 'reports/' + reportId);
      
      await update(newReportRef, {
        reportId,
        uid: currentUser.uid,
        userName: userProfile.name,
        userEmail: userProfile.email,
        suid: userProfile.suid || 'MISSING',
        category: 'Account Deletion Request',
        subject: 'Priority Account Purge Request',
        details: `User ${userProfile.name} (${userProfile.email}) has requested full account and footprint deletion. Processed via Origin Core multi-step verification.`,
        status: 'open',
        priority: 'medium',
        origin: 'Origin Core',
        scrutinyStatus: 'Processed by Origin Core',
        createdAt: Date.now()
      });

      setModalType(null);
      setPurgeStep(1);
      setPurgeConfirmText("");
      alert("Purge Audit Initiated. Administrators have been notified. Your data footprint is now under scrutiny.");
    } catch (e) {
      alert("Failed to initiate purge audit.");
    } finally {
      setSaving(false);
    }
  };

  const submitIdChangeRequest = async () => {
    if (!requestedId) return;
    
    // Check cost model
    const changeCount = userProfile.idChangesCount || 0;
    if (changeCount > 0 && (userProfile.points || 0) < 20) {
      alert("Insufficient AI Credits. 20 credits required for subsequent ID migrations.");
      return;
    }

    setSaving(true);
    try {
      // 1. Generate unique Case ID
      const caseId = await generateId('CASE');
      setGeneratedCaseId(caseId);

      // 2. Create a Report for Admin
      const reportRef = ref(db, 'reports');
      const newReportRef = push(reportRef);
      await set(newReportRef, {
        id: newReportRef.key,
        reportId: caseId, // Use Case ID as the visible report ID
        uid: currentUser.uid,
        userName: userProfile.name,
        userEmail: userProfile.email,
        category: 'Profile ID Migration',
        subject: `ID Change Request (${caseId})`,
        details: `User requested migration from current ID (${userProfile.profileId}) to new ID: ${requestedId}.`,
        status: 'open',
        priority: 'low',
        origin: 'Origin Core',
        requestedId: requestedId,
        currentId: userProfile.profileId,
        createdAt: Date.now()
      });

      // 3. Handle Credits Deduction if not first time
      if (changeCount > 0) {
        await update(ref(db, `users/${currentUser.uid}`), {
          points: increment(-20)
        });
        await sendTemplateNotification(currentUser.uid, 'ID_CHANGE_FEE', { caseId });
      }

      // 4. Update UI to Success State
      setIdChangeStep(5); // Success step
    } catch (e) {
      alert("Failed to submit ID migration request.");
    } finally {
      setSaving(false);
    }
  };

  if (!userProfile) return <div className="page-container"><div className="loader" style={{margin:'100px auto'}}></div></div>;

  const publicUrl = `${window.location.origin}/dev/${userProfile.profileId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(publicUrl + '?scan=true')}`;

  return (
    <div className="dashboard-container container animate-up">
      <div className="dashboard-header glass-card">
        <div className="profile-summary">
          <div className="dashboard-avatar-wrapper">
            {userProfile.avatar ? (
              <img src={userProfile.avatar} alt="Profile" className="dashboard-avatar" />
            ) : (
              <div className="dashboard-avatar-placeholder"><i className="fa-solid fa-user"></i></div>
            )}
            {unreadCount > 0 && <span className="unread-notif-dot"></span>}
          </div>
          <div className="profile-summary-text">
            <h1>Welcome, {userProfile.name}</h1>
            <div className="profile-id-badge">
              <i className="fa-solid fa-fingerprint"></i>
              Active Profile: <strong>{userProfile.profileId}</strong>
            </div>
          </div>
        </div>
        
        <div className="dashboard-actions" style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
          <div className="platform-stats-bubble" style={{background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', padding: '6px 15px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '8px'}}>
            <span style={{fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: '800', letterSpacing: '1px'}}>AI CREDITS:</span>
            <span style={{color: 'var(--brand-accent)', fontWeight: 'bold', fontSize: '1rem'}}>{userProfile.points || 0}</span>
          </div>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="btn btn-primary">
            <i className="fa-solid fa-arrow-up-right-from-square"></i> Live Profile
          </a>
        </div>
      </div>

      <nav className="dashboard-tabs">
        <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
          <i className="fa-solid fa-user"></i> Profile
        </button>
        <button className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')}>
          <i className="fa-solid fa-bell"></i> Notifications {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
        <button className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
          <i className="fa-solid fa-shield-halved"></i> Security & ID
        </button>
        <button className={`tab-btn ${activeTab === 'ai' ? 'active' : ''}`} onClick={() => setActiveTab('ai')}>
          <i className="fa-solid fa-wand-magic-sparkles"></i> AI Insights
        </button>
        <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          <i className="fa-solid fa-sliders"></i> Platform
        </button>
      </nav>

      {activeTab === 'notifications' && (
        <NotificationPanel 
          notifications={notifications}
          unreadCount={unreadCount}
          deleteNotification={deleteNotification}
          setSelectedNotif={setSelectedNotif}
        />
      )}

      {/* DASHBOARD MAIL MODAL */}
      {selectedNotif && (
        <div className="modal-overlay" onClick={() => setSelectedNotif(null)}>
          <div className={`notif-mail-modal glass-card theme-${selectedNotif.type} animate-up`} onClick={e => e.stopPropagation()} style={{maxWidth: '600px', width: '90%'}}>
            <div className="mail-modal-header" style={{padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <div className="mail-meta">
                <span className="mail-from" style={{fontSize: '0.75rem', color: 'var(--brand-accent)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px'}}>System Broadcast // DevTracker Core</span>
                <span className="mail-date" style={{fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block'}}>{new Date(selectedNotif.createdAt).toLocaleString()}</span>
              </div>
              <button onClick={() => setSelectedNotif(null)} style={{background: 'rgba(255,255,255,0.05)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: '50%', cursor: 'pointer'}}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            
            <div className="mail-content" style={{padding: '30px'}}>
              <div className="mail-subject" style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px'}}>
                <i className={`fa-solid ${
                    selectedNotif.type === 'success' ? 'fa-circle-check' : 
                    selectedNotif.type === 'warning' ? 'fa-triangle-exclamation' :
                    selectedNotif.type === 'critical' ? 'fa-circle-exclamation' : 'fa-circle-info'
                  }`} style={{fontSize: '1.8rem', color: `var(--notif-${selectedNotif.type === 'danger' ? 'critical' : selectedNotif.type})`}}></i>
                <h2 style={{margin: 0, fontSize: '1.4rem'}}>{selectedNotif.title}</h2>
              </div>
              <div className="mail-body" style={{fontSize: '1rem', color: '#e5e7eb', lineHeight: '1.6', whiteSpace: 'pre-line'}}>
                {selectedNotif.message}
              </div>
            </div>

            <div className="mail-footer" style={{padding: '20px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  deleteNotification(selectedNotif.notificationId);
                  setSelectedNotif(null);
                }}
                style={{padding: '8px 15px', fontSize: '0.85rem', color: '#ff375f', border: '1px solid rgba(255, 55, 95, 0.2)'}}
              >
                <i className="fa-solid fa-trash-can"></i> Discard
              </button>
              <button className="btn btn-primary" onClick={() => setSelectedNotif(null)} style={{padding: '8px 25px', fontSize: '0.85rem'}}>
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ai' && (
        <AIAnalyst stats={{
          github: userProfile.githubUsername ? 100 : 0, 
          leetcode: userProfile.leetcodeUsername ? 150 : 0,
          codeforces: userProfile.codeforcesUsername ? 1200 : 0
        }} />
      )}

      {activeTab === 'profile' && (
        <div className="dashboard-grid animate-up">
          <div className="sharing-column">
            <div className="sharing-card glass-card">
              <h3>Share Profile</h3>
              <div className="qr-container">
                <img src={qrUrl} alt="Public Profile QR Code" />
              </div>
              <p style={{fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '10px'}}><i className="fa-solid fa-circle-dollar-to-slot"></i> Scan-to-Earn Rewards Active</p>
              
              <div className="share-link-box">
                <span>{publicUrl}</span>
                <button onClick={() => {
                  navigator.clipboard.writeText(publicUrl);
                  setMessage("Link copied to clipboard!");
                  setTimeout(() => setMessage(""), 3000);
                }} className="btn btn-secondary" style={{padding: '5px 10px', fontSize: '0.8rem'}}>
                  Copy
                </button>
              </div>
            </div>

            <div className="resume-banner glass-card" style={{marginTop: '25px'}}>
              <div className="resume-info">
                <h3>Resume Mode</h3>
                <p>Convert your profile into a professional bio.</p>
              </div>
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                <button 
                  onClick={toggleResumeMode} 
                  className={`btn ${resumeMode ? 'btn-primary' : 'btn-secondary'}`}
                  style={{padding: '8px 15px', width: '100%'}}
                >
                  {resumeMode ? 'Disable' : 'Enable'}
                </button>
                {resumeMode && (
                  <>
                    <button 
                      onClick={() => setShowExtractModal(true)} 
                      className="btn btn-outline" 
                      style={{padding: '8px 15px', fontSize: '0.85rem', width: '100%', borderColor: '#00ffae', color: '#00ffae', marginBottom: '8px'}}
                    >
                      <i className="fa-solid fa-wand-magic-sparkles"></i> Neural Extract
                    </button>
                    <a href={`/resume/${userProfile.profileId}`} target="_blank" rel="noreferrer" className="btn btn-outline" style={{padding: '8px 15px', fontSize: '0.85rem', textAlign: 'center', border: '1px solid var(--brand-accent)', color: 'var(--brand-accent)'}}>
                      <i className="fa-solid fa-file-pdf"></i> View Resume
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          <ProfileEditor 
            formData={formData}
            handleChange={handleChange}
            handleUpdate={handleUpdate}
            handleArrayUpdate={handleArrayUpdate}
            saving={saving}
            message={message}
          />
        </div>
      )}

      {activeTab === 'security' && (
        <SecurityPanel 
          userProfile={userProfile}
          setModalType={setModalType}
          setIdChangeStep={setIdChangeStep}
          myReports={myReports}
          setSelectedTrackingCase={setSelectedTrackingCase}
        />
      )}

      {activeTab === 'settings' && (
        <div className="platform-control-center animate-up">
          <div className="control-center-grid">
            {/* Intelligence Hub: Embeds & Notifications */}
            <div className="control-node-column">
              <div className="control-node glass-card">
                <div className="node-header">
                  <i className="fa-solid fa-code"></i>
                  <h4>Intelligence Embeds</h4>
                </div>
                <p className="node-desc">Provision stat widgets for external endpoints.</p>
                
                <div className="form-group">
                  <label className="form-label">Theme Engine</label>
                  <select 
                    className="form-input" 
                    name="embedTheme"
                    value={settingsData.embedTheme} 
                    onChange={handleSettingsChange}
                  >
                    <option value="dark">Dark Professional</option>
                    <option value="light">Light Minimalist</option>
                  </select>
                </div>

                <div className="embed-preview-window">
                  <iframe 
                    src={`${window.location.origin}/embed/${userProfile.profileId}?theme=${settingsData.embedTheme}`} 
                    width="100%" 
                    height="100%" 
                    frameBorder="0"
                    title="Embed Preview"
                  ></iframe>
                </div>

                <button onClick={handleCopyEmbed} className="btn btn-secondary" style={{width: '100%', marginTop: '15px'}}>
                  <i className={`fa-solid ${copiedEmbed ? 'fa-check' : 'fa-link'}`}></i> {copiedEmbed ? 'Cipher Copied' : 'Copy Embed Protocol'}
                </button>
              </div>

              <div className="control-node glass-card">
                <div className="node-header">
                  <i className="fa-solid fa-bell"></i>
                  <h4>Neural Broadcasts</h4>
                </div>
                <p className="node-desc">Platform-wide alert synchronization.</p>
                
                <div className="broadcast-toggles">
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <strong>Security High-Priority</strong>
                      <span>Authentication & Audit alerts</span>
                    </div>
                    <label className="switch">
                      <input type="checkbox" name="notifySecurity" checked={settingsData.notifySecurity} onChange={handleSettingsChange} />
                      <span className="slider round"></span>
                    </label>
                  </div>

                  <div className="toggle-item">
                    <div className="toggle-info">
                      <strong>Recursive Updates</strong>
                      <span>New DAIV features & logic</span>
                    </div>
                    <label className="switch">
                      <input type="checkbox" name="notifyUpdates" checked={settingsData.notifyUpdates} onChange={handleSettingsChange} />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Governance Hub: Privacy & Security */}
            <div className="control-node-column">
              <div className="control-node glass-card primary-border">
                <div className="node-header">
                  <i className="fa-solid fa-eye-slash"></i>
                  <h4>Privacy Governance</h4>
                </div>
                <p className="node-desc">Manage your professional visibility matrix.</p>
                
                <div className="form-group">
                  <label className="form-label">Footprint Visibility</label>
                  <select 
                    className="form-input" 
                    name="profileVisibility"
                    value={settingsData.profileVisibility} 
                    onChange={handleSettingsChange}
                  >
                    <option value="public">🌐 Public Matrix</option>
                    <option value="url_only">🔗 Direct Protocol Only</option>
                    <option value="private">🔒 Encrypted Archive (Private)</option>
                  </select>
                </div>
                
                <div className="status-indicator-box">
                  <div className="status-dot pulse"></div>
                  <span>Profile is currently <strong>{settingsData.profileVisibility}</strong></span>
                </div>

                <button className="btn btn-primary" onClick={handleUpdate} disabled={saving} style={{width: '100%', marginTop: '20px'}}>
                   {saving ? 'Syncing...' : 'Commit Settings to Core'}
                </button>
              </div>

              <div className="control-node glass-card referral-node">
                <div className="node-header">
                  <i className="fa-solid fa-dna"></i>
                  <h4 style={{color: 'var(--brand-accent)'}}>Referral Engine</h4>
                </div>
                <p className="node-desc">Recruit peers to earn AI Credits.</p>
                
                <div className="invite-box">
                  <input readOnly value={`${window.location.origin}/register?ref=${userProfile.uid}`} className="invite-input" />
                  <button onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/register?ref=${userProfile.uid}`);
                      setMessage("Invite Cipher Copied");
                      setTimeout(() => setMessage(""), 3000);
                  }} className="invite-copy-btn">Copy</button>
                </div>

                <div className="referral-track">
                  {[1, 3, 5].map(step => (
                    <div key={step} className={`track-step ${userProfile.referrals?.count >= step ? 'completed' : ''}`}>
                      <div className="step-point"></div>
                      <span className="step-label">{step} REF</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="control-node-column">
              <div className="control-node glass-card danger-node">
                <div className="node-header">
                  <i className="fa-solid fa-skull-crossbones"></i>
                  <h4 style={{color: '#ff375f'}}>Danger Zone</h4>
                </div>
                <p className="node-desc">Initiate irreversible profile dissolution protocols.</p>
                
                <div className="danger-actions">
                  <button onClick={() => { setModalType('purge'); setPurgeStep(1); }} className="purge-trigger-btn">
                    <i className="fa-solid fa-fire"></i> Primary Account Purge
                  </button>
                  <p className="danger-note"><i className="fa-solid fa-circle-info"></i> All external platform statistics and professional data will be scrubbed.</p>
                </div>
              </div>
              
              <div className="system-uptime-card glass-card">
                 <div style={{display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.8rem'}}>
                    <i className="fa-solid fa-circle-check" style={{color: '#00ffae'}}></i>
                    Origin Core: PLATFORM_STABLE
                 </div>
                 <div style={{fontSize: '0.7rem', color: '#8b949e', marginTop: '5px'}}>SECURE_VERSION: {window.APP_VERSION || 'v4.2.0'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purge Modal */}
      {modalType === 'purge' && (
        <div className="modal-overlay">
          <div className="glass-card purge-modal animate-up">
            <div className="purge-modal-header">
              <h3><i className="fa-solid fa-skull-crossbones"></i> Account Purge Audit</h3>
              <button onClick={() => setModalType(null)} className="close-modal"><i className="fa-solid fa-xmark"></i></button>
            </div>
            
            <div className="purge-modal-body">
              <div className="purge-steps">
                <div className={`step-dot ${purgeStep >= 1 ? 'active' : ''}`}>1</div>
                <div className="step-line"></div>
                <div className={`step-dot ${purgeStep >= 2 ? 'active' : ''}`}>2</div>
                <div className="step-line"></div>
                <div className={`step-dot ${purgeStep >= 3 ? 'active' : ''}`}>3</div>
                <div className="step-line"></div>
                <div className={`step-dot ${purgeStep >= 4 ? 'active' : ''}`}>4</div>
              </div>

              {purgeStep === 1 && (
                <div className="step-content">
                  <h4><i className="fa-solid fa-triangle-exclamation"></i> IRREVERSIBLE ACTION</h4>
                  <p>This action is absolute. All coding streaks, platform integrations, and personalized metrics will be permanently purged from our database.</p>
                  <div className="purge-warning-box">
                    No data can be recovered after the audit is finalized.
                  </div>
                  <button className="btn btn-primary" onClick={() => setPurgeStep(2)} style={{width: '100%'}}>Proceed to Data Inventory</button>
                </div>
              )}

              {purgeStep === 2 && (
                <div className="step-content">
                  <h4><i className="fa-solid fa-box-open"></i> DATA INVENTORY</h4>
                  <p>The following footprints will be flushed from DevTracker arrays:</p>
                  <ul className="purge-list">
                    <li><i className="fa-brands fa-github"></i> GitHub Sync Data & History</li>
                    <li><i className="fa-solid fa-code"></i> LeetCode Statistics</li>
                    <li><i className="fa-solid fa-chart-simple"></i> Codeforces Activity</li>
                    <li><i className="fa-solid fa-fingerprint"></i> Public Profile & EID/SUID</li>
                  </ul>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button className="btn btn-secondary" onClick={() => setPurgeStep(1)} style={{flex: 1}}>Back</button>
                    <button className="btn btn-primary" onClick={() => setPurgeStep(3)} style={{flex: 2}}>Confirm Inventory</button>
                  </div>
                </div>
              )}

              {purgeStep === 3 && (
                <div className="step-content">
                  <h4><i className="fa-solid fa-shield-check"></i> IDENTITY VERIFICATION</h4>
                  <p>To prevent accidental deletion, please type <strong>PURGE</strong> in the field below to unlock the final audit request.</p>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="Type PURGE here"
                    value={purgeConfirmText}
                    onChange={(e) => setPurgeConfirmText(e.target.value)}
                    style={{textAlign: 'center', fontSize: '1.2rem', letterSpacing: '2px', marginBottom: '20px', border: purgeConfirmText === 'PURGE' ? '1px solid var(--brand-accent)' : '1px solid #ff375f'}}
                  />
                  <div style={{display: 'flex', gap: '10px'}}>
                    <button className="btn btn-secondary" onClick={() => setPurgeStep(2)} style={{flex: 1}}>Back</button>
                    <button 
                      className="btn btn-primary" 
                      disabled={purgeConfirmText !== 'PURGE'} 
                      onClick={() => setPurgeStep(4)} 
                      style={{flex: 2}}
                    >
                      Unlock Final Step
                    </button>
                  </div>
                </div>
              )}

              {purgeStep === 4 && (
                <div className="step-content">
                  <h4><i className="fa-solid fa-fire-flame-curved"></i> INITIATE AUDIT</h4>
                  <p>You are about to generate a priority account purge report for DevTracker Administrators. The <strong>Origin Core</strong> will begin scrubbing your footprint upon approval.</p>
                  <button className="btn btn-danger" onClick={requestAccountPurge} disabled={saving} style={{width: '100%', background: '#ff375f', color: '#fff'}}>
                    {saving ? 'Initiating...' : 'Submit Final Purge Request'}
                  </button>
                  <button className="btn btn-link" onClick={() => setModalType(null)} style={{width: '100%', marginTop: '10px', color: '#8b949e', background: 'none', border: 'none', cursor: 'pointer'}}>Cancel Request</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile ID Change Workflow Modal */}
      {modalType === 'idchange' && (
        <div className="modal-overlay">
          <div className="glass-card purge-modal animate-up" style={{maxWidth: '550px'}}>
            <div className="purge-modal-header" style={{borderBottom: '1px solid var(--border-color)', paddingBottom: '15px'}}>
              <h3><i className="fa-solid fa-id-card-clip"></i> Profile ID Migration Audit</h3>
              <button onClick={() => setModalType(null)} className="close-modal"><i className="fa-solid fa-xmark"></i></button>
            </div>
            
            <div className="purge-modal-body" style={{marginTop: '25px'}}>
              <div className="purge-steps" style={{marginBottom: '30px'}}>
                <div className={`step-dot ${idChangeStep >= 1 ? 'active' : ''}`}>1</div>
                <div className="step-line"></div>
                <div className={`step-dot ${idChangeStep >= 2 ? 'active' : ''}`}>2</div>
                <div className="step-line"></div>
                <div className={`step-dot ${idChangeStep >= 3 ? 'active' : ''}`}>3</div>
                <div className="step-line"></div>
                <div className={`step-dot ${idChangeStep >= 4 ? 'active' : ''}`}>4</div>
                <div className="step-line"></div>
                <div className="step-dot"><i className="fa-solid fa-flag-checkered"></i></div>
              </div>

              {idChangeStep === 1 && (
                <div className="step-content">
                  <h4 style={{color: '#fff', marginBottom: '10px'}}><i className="fa-solid fa-circle-info"></i> ID MIGRATION POLICY</h4>
                  <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)'}}>Profile IDs are the permanent anchors of your DevTracker identity.</p>
                  <div className="docs-note info" style={{margin: '15px 0', background: 'rgba(0, 255, 174, 0.05)', border: '1px solid rgba(0, 255, 174, 0.1)', color: 'var(--brand-accent)'}}>
                    <i className="fa-solid fa-gem"></i> Cost: <strong>{userProfile.idChangesCount > 0 ? '20 AI CREDITS' : 'FREE (1st Change)'}</strong>
                  </div>
                  <ul className="purge-list" style={{margin: '15px 0', fontSize: '0.85rem'}}>
                    <li>Break all existing public links & social shares.</li>
                    <li>Require a manual update of all Embed Widgets.</li>
                    <li>Invalidate all previously generated QR codes.</li>
                  </ul>
                  <button className="btn btn-primary" onClick={() => setIdChangeStep(2)} style={{width: '100%', marginTop: '10px'}}>Proceed to Eligibility Check</button>
                </div>
              )}

              {idChangeStep === 2 && (
                <div className="step-content">
                  <h4 style={{color: '#fff', marginBottom: '10px'}}><i className="fa-solid fa-shield-halved"></i> ELIGIBILITY SCRUTINY</h4>
                  <div className="purge-warning-box" style={{background: 'rgba(0,255,174,0.05)', border: '1px solid rgba(0,255,174,0.1)', color: 'var(--brand-accent)'}}>
                    <i className="fa-solid fa-check-circle"></i> Account Standing: <strong>EXCELLENT</strong>
                  </div>
                  <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '15px'}}>Our automated scanners confirm your account is in good standing. No security flags or pending moderation cases detected.</p>
                  <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                    <button className="btn btn-secondary" onClick={() => setIdChangeStep(1)} style={{flex: 1}}>Back</button>
                    <button className="btn btn-primary" onClick={() => setIdChangeStep(3)} style={{flex: 2}}>Propose New Identifier</button>
                  </div>
                </div>
              )}

              {idChangeStep === 3 && (
                <div className="step-content">
                  <h4 style={{color: '#fff', marginBottom: '10px'}}><i className="fa-solid fa-pen-to-square"></i> PROPOSE IDENTIFIER</h4>
                  <p style={{fontSize: '0.85rem', color: '#8b949e', marginBottom: '15px'}}>Identifiers must follow the <code>DEV-XXXXXX</code> format or be a custom alpha-numeric string (Alpha/Beta only).</p>
                  <div className="form-group">
                    <label className="form-label">Requested New ID</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="e.g. DEV-CHG882"
                      value={requestedId}
                      onChange={(e) => setRequestedId(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                      style={{background: '#0d1117', fontSize: '1.1rem', letterSpacing: '1px'}}
                    />
                  </div>
                  <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                    <button className="btn btn-secondary" onClick={() => setIdChangeStep(2)} style={{flex: 1}}>Back</button>
                    <button className="btn btn-primary" disabled={!requestedId || requestedId.length < 4} onClick={() => setIdChangeStep(4)} style={{flex: 2}}>Submit for Admin Review</button>
                  </div>
                </div>
              )}

              {idChangeStep === 4 && (
                <div className="step-content">
                  <h4 style={{color: '#fff', marginBottom: '10px'}}><i className="fa-solid fa-paper-plane"></i> INITIATE MIGRATION</h4>
                  <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)'}}>By submitting, you initiate a Phase 2 manual audit. An administrator will review your requested ID <strong>{requestedId}</strong> against platform standards.</p>
                  <div className="docs-note warning" style={{marginTop: '15px'}}>
                    <i className="fa-solid fa-clock-rotate-left"></i> Typical review time: 6-24 hours.
                  </div>
                  {userProfile.idChangesCount > 0 && (
                     <div style={{marginTop: '15px', fontSize: '0.8rem', color: '#8b949e', textAlign: 'center'}}>
                        Fee: <span style={{color: 'var(--brand-accent)'}}>20 AI Credits</span> will be deducted.
                     </div>
                  )}
                  <button className="btn btn-primary" style={{width: '100%', marginTop: '20px'}} onClick={submitIdChangeRequest} disabled={saving}>
                    {saving ? 'Submitting Request...' : 'Submit Request for Approval'}
                  </button>
                </div>
              )}

              {idChangeStep === 5 && (
                <div className="step-content animate-up" style={{textAlign: 'center', padding: '20px 0'}}>
                   <div className="success-icon-wrapper" style={{background: 'rgba(0, 255, 174, 0.1)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '1px solid var(--brand-accent)'}}>
                      <i className="fa-solid fa-check" style={{color: 'var(--brand-accent)', fontSize: '2rem'}}></i>
                   </div>
                   <h3 style={{color: '#fff', marginBottom: '10px'}}>Request Dispatched</h3>
                   <div style={{background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)', margin: '20px 0'}}>
                      <span style={{fontSize: '0.8rem', color: '#8b949e', letterSpacing: '1px'}}>TRACKING IDENTIFIER</span>
                      <div style={{fontSize: '1.5rem', color: 'var(--brand-accent)', fontWeight: 'bold', fontFamily: 'monospace', marginTop: '5px'}}>{generatedCaseId}</div>
                   </div>
                   <p style={{fontSize: '0.85rem', color: 'var(--text-secondary)'}}>Follow the status in your <strong>Security & ID</strong> tab. You will be notified via Origin Core once the migration audit is complete.</p>
                   <button className="btn btn-secondary" style={{width: '100%', marginTop: '30px'}} onClick={() => setModalType(null)}>Acknowledge & Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Report Modal */}
      {modalType === 'report' && (
        <div className="modal-overlay">
          <div className="glass-card report-modal animate-up" style={{width: '90%', maxWidth: '500px', padding: '0', overflow: 'hidden'}}>
            <div className="modal-header" style={{padding: '20px 25px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h3 style={{margin:0, color:'var(--brand-accent)'}}><i className="fa-solid fa-bug"></i> Quick Report</h3>
              <button onClick={() => setModalType(null)} className="close-modal"><i className="fa-solid fa-xmark"></i></button>
            </div>
            
            <form onSubmit={submitQuickReport} style={{padding: '30px'}}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select 
                  className="form-input" 
                  value={reportData.category} 
                  onChange={(e) => setReportData({...reportData, category: e.target.value})}
                  style={{background: '#0d1117'}}
                >
                  <option value="bug">🐛 Bug Report</option>
                  <option value="ui_ux">🎨 UI/UX Feedback</option>
                  <option value="security">🛡️ Security Concern</option>
                  <option value="integration">🔌 Integration Issue</option>
                  <option value="other">💬 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Subject</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="Short summary of the issue"
                  value={reportData.subject}
                  onChange={(e) => setReportData({...reportData, subject: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea 
                  className="form-input" 
                  placeholder="Provide details so we can help you faster..."
                  rows="4"
                  value={reportData.details}
                  onChange={(e) => setReportData({...reportData, details: e.target.value})}
                  style={{resize: 'none', background: '#0d1117'}}
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary" style={{width: '100%', marginTop: '10px'}} disabled={saving}>
                {saving ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </div>
        </div>
      )}

      {selectedTrackingCase && <TrackingModal report={selectedTrackingCase} onClose={() => setSelectedTrackingCase(null)} />}

      {/* NEURAL EXTRACT MODAL */}
      {showExtractModal && (
        <div className="modal-overlay" onClick={() => !isExtracting && setShowExtractModal(false)}>
          <div className="glass-card animate-up" onClick={e => e.stopPropagation()} style={{maxWidth: '700px', width: '95%', padding: '0', overflow: 'hidden', border: '1px solid rgba(0, 255, 174, 0.2)'}}>
            <div style={{padding: '25px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0, 255, 174, 0.05)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                   <i className="fa-solid fa-wand-magic-sparkles" style={{color: '#00ffae', fontSize: '1.5rem'}}></i>
                   <div>
                      <h2 style={{fontSize: '1.2rem', color: '#fff', margin: 0}}>DAIV-X Neural Extraction</h2>
                      <p style={{color: '#8b949e', fontSize: '0.8rem', margin: 0}}>MAPPING_INTELLIGENCE: High-fidelity document parsing active.</p>
                   </div>
                </div>
                <button disabled={isExtracting} onClick={() => setShowExtractModal(false)} style={{background: 'rgba(255,255,255,0.05)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: '50%', cursor: 'pointer'}}>
                    <i className="fa-solid fa-xmark"></i>
                </button>
            </div>

            <div style={{padding: '30px'}}>
              {extractionError && (
                <div style={{background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '12px 15px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9rem'}}>
                  <i className="fa-solid fa-triangle-exclamation" style={{marginRight: '8px'}}></i> {extractionError}
                </div>
              )}

              {/* MODE SWITCHER */}
              <div style={{display: 'flex', gap: '10px', marginBottom: '20px', background: 'rgba(255,255,255,0.03)', padding: '5px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.05)'}}>
                <button 
                  onClick={() => setIsTextMode(false)}
                  style={{flex: 1, padding: '10px', borderRadius: '25px', background: !isTextMode ? 'rgba(0, 255, 174, 0.1)' : 'transparent', border: 'none', color: !isTextMode ? '#00ffae' : '#8b949e', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.3s'}}
                >
                  <i className="fa-solid fa-file-invoice"></i> DOCUMENT SYNC
                </button>
                <button 
                  onClick={() => setIsTextMode(true)}
                  style={{flex: 1, padding: '10px', borderRadius: '25px', background: isTextMode ? 'rgba(0, 255, 174, 0.1)' : 'transparent', border: 'none', color: isTextMode ? '#00ffae' : '#8b949e', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.3s'}}
                >
                  <i className="fa-solid fa-keyboard"></i> MANUAL PASTE
                </button>
              </div>

              <div className="extraction-input-container" style={{position: 'relative', marginBottom: '25px'}}>
                {isExtracting && (
                  <div className="extracting-overlay" style={{position: 'absolute', inset: 0, background: 'rgba(13, 17, 23, 0.95)', zIndex: 10, borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)'}}>
                     <div className={`resume-scanning-animation ${extractionType}`}></div>
                     <p style={{color: '#00ffae', fontWeight: 'bold', marginTop: '20px', letterSpacing: '2px', fontSize: '0.7rem', textAlign: 'center'}} className="flicker">
                        {extractionType === 'pdf' ? 'PARSING_PDF_PROTOCOLS...' : extractionType === 'image' ? 'OCR_ENGINE_STREAMS_ACTIVE...' : 'MAPPING_PROFESSIONAL_DNA...'}
                        <br/>
                        <span style={{fontSize: '10px', opacity: 0.6, marginTop: '5px', display: 'block'}}>SECURE_DAIVX_CONNECTION: ESTABLISHED</span>
                     </p>
                  </div>
                )}
                
                {isTextMode ? (
                  <>
                    <label className="form-label">RAW_TERMINAL_INGRESS</label>
                    <textarea 
                      className="form-input" 
                      style={{minHeight: '250px', fontSize: '0.85rem', fontFamily: 'monospace', background: 'rgba(10, 10, 15, 0.4)', color: '#00ffae', border: '1px solid rgba(0, 255, 174, 0.1)'}}
                      placeholder="--- PASTE RAW RESUME CONTENT HERE ---"
                      value={extractionText}
                      onChange={e => setExtractionText(e.target.value)}
                    ></textarea>
                  </>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current.click()}
                    style={{
                      height: '250px', 
                      border: '2px dashed rgba(0, 255, 174, 0.2)', 
                      borderRadius: '12px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      background: 'rgba(0, 255, 174, 0.02)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={e => e.currentTarget.style.borderColor = '#00ffae'}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(0, 255, 174, 0.2)'}
                  >
                    <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.png,.jpg,.jpeg,.txt" />
                    <i className="fa-solid fa-cloud-arrow-up" style={{fontSize: '3rem', color: 'rgba(0, 255, 174, 0.5)', marginBottom: '15px'}}></i>
                    <h3 style={{fontSize: '1rem', color: '#fff', margin: '0 0 10px 0'}}>
                      {extractionFile ? extractionFile.name : 'Target Document Search'}
                    </h3>
                    <p style={{fontSize: '0.8rem', color: '#8b949e', margin: 0}}>
                      {extractionFile ? `Size: ${(extractionFile.size / 1024).toFixed(1)} KB` : 'Click to locate PDF or Image (PNG/JPG)'}
                    </p>
                    {extractionFile && (
                      <div style={{marginTop: '20px', padding: '5px 15px', borderRadius: '20px', background: 'rgba(0, 255, 174, 0.1)', color: '#00ffae', fontSize: '0.7rem', fontWeight: 'bold'}}>
                        <i className="fa-solid fa-check-double"></i> READY_FOR_EXTRACTION
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{display: 'flex', gap: '15px'}}>
                <button 
                  className="btn btn-primary" 
                  style={{flex: 1, padding: '14px', borderRadius: '12px'}} 
                  disabled={isExtracting || (!isTextMode && !extractionFile) || (isTextMode && !extractionText)}
                  onClick={handleExtract}
                >
                  {isExtracting ? (
                    <><i className="fa-solid fa-circle-notch fa-spin"></i> Initializing Neural Flow...</>
                  ) : (
                    <><i className="fa-solid fa-rocket"></i> Launch DAIV-X Extraction</>
                  )}
                </button>
                <button 
                  className="btn btn-secondary" 
                  style={{padding: '14px 25px', borderRadius: '12px'}} 
                  disabled={isExtracting}
                  onClick={() => {
                    setShowExtractModal(false);
                    setExtractionFile(null);
                    setExtractionText("");
                  }}
                >
                  Abort
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
